import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

// Libraries
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";

// Context
import { InstanceContext } from "../../../context/InstanceContext";
import { useLicences } from "../../../context/LicenceContext";

// Helpers
import Helper from "../../../helpers/Helper";
import {
    fetchResults, formatDateForApi, getAggregateInt, getTimeZone,
} from "../../../helpers/utils";
import ActivityApi from "../../../api/instance/ActivityApi";
import { showError } from '../../../utils/showAlert';
import useChartOptions from "../../../helpers/useChartOptions";
import ConditionalRender from "../../../helpers/ConditionalRender";

// Types
import { InstanceOptions, ActivityResultsTableProps } from "./types";
import { OracleSqlHashTimeSlice, OracleSqlHashWithMetric } from "../../statistics/types";

// Constants
import { CHART_DATA_TYPE } from "../../../component/Constants";

const ActivityComparisonWaitsChart = (props: ActivityResultsTableProps) => {
    const { instances } = useContext(InstanceContext);
    const { licences } = useLicences();

    const [loading, setLoading] = useState(false)
    const [topNChartData, setTopNChartData] = useState<{
        [instanceIndex: string | number]: { [key: string]: { values: OracleSqlHashTimeSlice[], orderIndex: number } }
    }>({});

    const timezone = getTimeZone()

    const chart1Ref = useRef(null);
    const chart2Ref = useRef(null);

    const aggregateInt = useMemo(() => getAggregateInt(props.options.aggregate, props.instanceOptions, instances), [props.options.aggregate])

    const getInstanceResults = useCallback(async(instanceOption: InstanceOptions) => {
        try {
            let chartApiDataResults: OracleSqlHashWithMetric[][] = [[]]
            const from = formatDateForApi(instanceOption.periodStart, timezone)
            const to = formatDateForApi(instanceOption.periodEnd, timezone)
            let customIdApiString = ''
            if (props.options.customSqlId) {
                customIdApiString = props.options.showSqlId ? `&sqlid=${props.options.customSqlId}` : `&sqlhash=${props.options.customSqlId}`
            }
            chartApiDataResults = await fetchResults([ActivityApi.fetchWaitEvents(from, to, timezone, instanceOption.instanceId, aggregateInt, `&statistic=executions&sort=waittime+desc&type=${instanceOption.instanceType}&${customIdApiString}`)])

            let topNChartApiDataResults: OracleSqlHashWithMetric[] = []
            topNChartApiDataResults = chartApiDataResults[0].slice(0, props.options.topN)


            let topNChartTimePromises = []
            for (let sqlHashData of topNChartApiDataResults) {
                const apiSqlHash = sqlHashData.sqlhash ? `sqlhash=${sqlHashData.sqlhash}` : ''
                const encodedWaitEvent = sqlHashData.waitevent ? encodeURIComponent(sqlHashData.waitevent) : ''
                topNChartTimePromises.push(ActivityApi.fetchTime(from, to, timezone, instanceOption.instanceId, aggregateInt, `&waitevent=${encodedWaitEvent}&id=${instanceOption.instanceId}&${apiSqlHash}`))
            }

            const topNChartTimeResults = await fetchResults(topNChartTimePromises)

            // sort by time
            topNChartTimeResults.forEach(chartTime => {
                chartTime.sort(function compare(a: OracleSqlHashTimeSlice, b: OracleSqlHashTimeSlice) {
                    const aa: number = Helper.parseTimesliceWithTz(String(a.timeslice)).valueOf();
                    const bb: number = Helper.parseTimesliceWithTz(String(b.timeslice)).valueOf();
                    return aa < bb ? -1 : 1;
                })
            })

            const topNData: { [key: string]: { values: OracleSqlHashTimeSlice[], orderIndex: number } } = {}

            topNChartTimeResults.forEach((result: OracleSqlHashTimeSlice[], index: number) => {
                const sqlhash = topNChartApiDataResults[index].waitevent

                if (sqlhash) {
                    topNData[sqlhash] = {
                        values: result.map((item: any) => {
                            return {
                                timeslice: item.timeslice,
                                waittime: item.waittime,
                            }
                        }),
                        orderIndex: index + 1
                    }
                }
            })
            return topNData
        } catch (error: any) {
            console.error(error)
            const errorMessage = error.message || error.toString();
            showError('Failed to load activity comparison waits chart', errorMessage);
        }
    }, [topNChartData, props.generateId])

    useEffect(() => {
        const start = async() => {
            try {
                setLoading(true)
                const promiseArray: any[] = []
                const dataToSet: any = {}
                props.instanceOptions.forEach((instanceOption: InstanceOptions) => {
                    promiseArray.push(getInstanceResults(instanceOption))
                })

                const results = await Promise.all(promiseArray)

                results.forEach((result, index: number) => {
                    dataToSet[index] = result || []
                })
                setTopNChartData(dataToSet)
                setLoading(false)
            } catch (error: any) {
                console.error('Failed to start activity comparison waits chart:', error);
                const errorMessage = error.message || error.toString();
                showError('Failed to load activity comparison waits chart data', errorMessage);
                setLoading(false);
                setTopNChartData({});
            }
        }
        void start()
    }, [props.generateId])

    const chartOptions = useChartOptions(
        topNChartData,
        props.instanceOptions,
        {
            metric: 'waittime',
            tooltip: {
                formatter: function () {
                    return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.TIME);
                },
            },
            chart: props.options.chart,
            combinedChanges: props.combinedChanges,
            interval: aggregateInt,
            type: 'waits',
            height: `300px`,
            licences: licences
        },
        props.generateId,
        undefined,
        props.setWaitsColors

    );

    return <div className="card">
        <div className="card-header">
            <ConditionalRender if={loading}>
                <div className="loader"
                     style={{ borderWidth: 0, position: "absolute", width: '80%', margin: 'auto', height: "5px" }}>
                    <div className="bar"/>
                </div>
            </ConditionalRender>
            <i className="fal fa-scroll fa-fw" aria-hidden="true"/>
            Top waits over time
            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
               data-bs-target="#collapseWaitsChart" aria-expanded="false"
               aria-controls="collapseWaitsChart"/>
        </div>
        <ConditionalRender if={chartOptions[0] && chartOptions[1]}>
            <div id="collapseWaitsChart" className="card-body collapse show combined-charts pt-5">
                <HighchartsReact ref={chart1Ref} constructorType={"chart"} highcharts={Highcharts}
                                 options={chartOptions[0]}/>
                <div className={"separator"}/>
                <HighchartsReact ref={chart2Ref} constructorType={"chart"} highcharts={Highcharts}
                                 options={chartOptions[1]}/>
            </div>
        </ConditionalRender>
    </div>
}

export default ActivityComparisonWaitsChart;
